import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { postModuleAttempt, uploadRecitation, submitForPeerReview } from "@/lib/apiClient";
import { checkAchievements } from "@/lib/achievements";
import { pickMime } from "@/lib/audioRecording";
import ModuleFeedbackPrompt from "@/components/ModuleFeedbackPrompt";
import PeerReviewTargetPicker from "@/components/PeerReviewTargetPicker";
import { useI18n } from "@/contexts/I18nContext";
import SectionContent, { deriveSectionContent } from "./SectionContent";
import {
  Clock, Play, Square, RotateCcw, CheckCircle2, XCircle, Mic, AlertTriangle,
  Trophy, Award, Users, Lock,
} from "lucide-react";

// A reading attempt must cover at least this fraction of the module's target
// time to count as a genuine read-through — mirrors backend/routes/modules.py.
const MIN_ATTEMPT_RATIO = 0.75;

// `hasSections`/`selectedSection`/`sectionUnlocked` come from ReadingMode's
// shared section picker (kept in sync with Learn mode) instead of being
// owned locally — Learn and Test must always agree on which section is
// selected, and Test additionally needs to know whether that section's
// Learn tab has been visited yet (the §5.3 unlock gate).
export default function TestSelfAssessment({ module: m, progress, onAttemptSaved, onRunningChange, hasSections, selectedSection, sectionUnlocked }) {
  const { t, lang } = useI18n();
  // Each section is tested and passed independently — there is no combined
  // "All" read-through when a module has sections. A module with no
  // sections at all has nothing to subdivide, so the single full-module
  // attempt remains its only test.
  const isFullSet = !hasSections;
  const [mode, setMode] = useState("idle"); // idle | running | done
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [fontStep, setFontStep] = useState(() => {
    const v = Number(localStorage.getItem("qf:font-step"));
    return Number.isFinite(v) && v >= -1 && v <= 2 ? v : 0;
  });
  const [recBlob, setRecBlob] = useState(null);
  const [recUrl, setRecUrl] = useState(null);
  const [recError, setRecError] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [peerSubmitting, setPeerSubmitting] = useState(false);
  const [peerSubmitted, setPeerSubmitted] = useState(false);
  const [peerTarget, setPeerTarget] = useState({ target_type: null, target_circle_id: null, ready: false });
  const mediaRecorder = useRef(null);
  const recChunks = useRef([]);
  const recStream = useRef(null);
  const startedAt = useRef(null);
  const ticker = useRef(null);
  // Section content (which items/groups belong to the current section, in
  // original order) is derived once here via the shared SectionContent
  // helper — the same function Admin > Recitations uses to show a past
  // submission's exact section, so the two can never drift apart.
  const { sectionGroups, visibleItems } = deriveSectionContent(m, isFullSet ? null : selectedSection);
  const sectionMeta = !isFullSet ? (m.section_meta || {})[selectedSection] : null;
  const sectionProgress = !isFullSet ? progress?.sections?.[selectedSection] : null;
  // A configured (non-proportional) section target takes priority — mirrors
  // the server's own precedence in post_module_attempt. Falls back to a
  // proportional split of the module's overall target when unconfigured.
  const subsetTargetSec = isFullSet
    ? m.target_seconds
    : (sectionMeta?.target_seconds || Math.max(1, Math.ceil(m.target_seconds * visibleItems.length / Math.max(m.items.length, 1))));
  const expertSec = isFullSet ? m.expert_seconds : sectionMeta?.expert_seconds;
  // `section_meta.<rule>.description` is usually a plain string, but some
  // sections carry a per-locale translation object (e.g. { en, ta, ur, sw })
  // instead — resolve it by the student's active language, falling back to
  // English, so it renders as text instead of crashing React.
  const _rawDesc = sectionMeta?.description;
  const sectionDescription = !_rawDesc ? null
    : typeof _rawDesc === "string" ? _rawDesc
    : (_rawDesc[lang] || _rawDesc.en || null);
  // If Learn-by-Group groups exist for the current section (or the whole
  // module, when sectionless), display those as one line per group —
  // matching how the student already learned them. Items not covered by
  // any group stay hidden here, matching Learn's own Group view.

  useEffect(() => () => {
    if (recUrl) URL.revokeObjectURL(recUrl);
    if (recStream.current) recStream.current.getTracks().forEach((tr) => tr.stop());
    if (ticker.current) clearInterval(ticker.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { onRunningChange && onRunningChange(mode === "running"); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearInterval(ticker.current);
    setResult(null); setMode("idle"); setElapsed(0);
  }, [selectedSection]);

  const fontSizeClass = ["text-xl sm:text-2xl", "text-2xl sm:text-3xl", "text-3xl sm:text-4xl", "text-4xl sm:text-5xl"][fontStep + 1] || "text-2xl sm:text-3xl";
  const groupFontSizeClass = ["text-2xl sm:text-3xl", "text-3xl sm:text-4xl", "text-4xl sm:text-5xl", "text-5xl sm:text-6xl"][fontStep + 1] || "text-3xl sm:text-4xl";

  const start = async () => {
    setMode("running"); setResult(null); setElapsed(0);
    setRecBlob(null);
    setRecError(null);
    if (recUrl) { URL.revokeObjectURL(recUrl); setRecUrl(null); }
    setSubmitted(null);
    setPeerSubmitted(false);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setRecError(t("rd.err_unsupported"));
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        recStream.current = stream;
        const recMime = pickMime();
        const rec = new MediaRecorder(stream, { mimeType: recMime });
        recChunks.current = [];
        rec.ondataavailable = (e) => e.data.size && recChunks.current.push(e.data);
        rec.onstop = () => {
          stream.getTracks().forEach((tr) => tr.stop());
          if (!recChunks.current.length) {
            setRecError(t("rd.err_empty"));
            return;
          }
          const blobType = rec.mimeType || recMime || recChunks.current[0]?.type || "audio/webm";
          const b = new Blob(recChunks.current, { type: blobType });
          if (b.size < 256) {
            setRecError(t("rd.err_tiny"));
            return;
          }
          setRecBlob(b);
          setRecUrl(URL.createObjectURL(b));
        };
        mediaRecorder.current = rec;
        rec.start();
      } catch (e) {
        mediaRecorder.current = null;
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") setRecError(t("rd.err_blocked"));
        else if (name === "NotFoundError" || name === "DevicesNotFoundError") setRecError(t("rd.err_no_mic"));
        else setRecError(t("rd.err_generic"));
      }
    }
    startedAt.current = Date.now();
    ticker.current = setInterval(() => setElapsed(Date.now() - startedAt.current), 100);
  };

  const stop = async () => {
    clearInterval(ticker.current);
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
      try { mediaRecorder.current.stop(); } catch (_e) { /* harmless */ }
    }
    const ms = Date.now() - startedAt.current;
    setElapsed(ms);
    setMode("done");

    try {
      const res = await postModuleAttempt(m.code, {
        module_code: m.code,
        elapsed_ms: ms,
        section: isFullSet ? undefined : selectedSection,
      });
      setResult(res);
      onAttemptSaved && onAttemptSaved();
      if (res.attempt.passed) {
        checkAchievements();
        toast.success(isFullSet
          ? t("rd.toast_pass", { s: (ms / 1000).toFixed(1) })
          : t("rd.toast_subset_pass", { sec: selectedSection, s: (ms / 1000).toFixed(1) }));
      } else if (res.attempt.fail_reason === "too_fast") {
        toast.error(isFullSet
          ? t("rd.toast_miss_too_fast", { min: Math.ceil(m.target_seconds * MIN_ATTEMPT_RATIO) })
          : t("rd.toast_subset_miss_too_fast", { sec: selectedSection, min: Math.ceil(subsetTargetSec * MIN_ATTEMPT_RATIO) }));
      } else {
        toast.error(isFullSet
          ? t("rd.toast_miss")
          : t("rd.toast_subset_miss", { sec: selectedSection, target: subsetTargetSec }));
      }
    } catch (_e) {
      toast.error(t("rd.toast_save_failed"));
    }
  };

  const reset = () => {
    clearInterval(ticker.current);
    setMode("idle"); setElapsed(0); setResult(null);
    setRecBlob(null);
    setRecError(null);
    if (recUrl) { URL.revokeObjectURL(recUrl); setRecUrl(null); }
    setSubmitted(null);
    setPeerSubmitted(false);
  };

  const submitToPeerReview = async () => {
    if (!recBlob || peerSubmitting || peerSubmitted || !peerTarget.ready) return;
    setPeerSubmitting(true);
    try {
      const note = t("rd.submit_note", { n: m.n, subset: !isFullSet ? ` · ${selectedSection}` : "" });
      // Mirrors RecordingMode.jsx's uploadRecitation call — reusing the same
      // passageCode/passageAr fields (section key + exact recited text) so
      // Admin > Recitations can show the original words for reading modules
      // too, not just module 9.
      const sectionArText = (sectionGroups.length > 0
        ? sectionGroups.flatMap((g) => g.item_indices.map((idx) => m.items[idx]?.ar))
        : visibleItems.map(({ it }) => it.ar)
      ).filter(Boolean).join(" ");
      const uploaded = submitted?.recitation_id
        ? submitted
        : await uploadRecitation(recBlob, `${m.code}-recording.webm`, note, m.code, isFullSet ? undefined : selectedSection, sectionArText);
      setSubmitted(uploaded);
      await submitForPeerReview({
        recitation_id: uploaded.recitation_id,
        module_code: m.code,
        section: isFullSet ? null : selectedSection,
        note,
        target_type: peerTarget.target_type,
        target_circle_id: peerTarget.target_circle_id,
      });
      setPeerSubmitted(true);
      toast.success(t("rec.toast_peer_open"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 409) toast.error(detail || t("rd.postpass_peer_already"));
      else toast.error(detail || t("rec.toast_peer_failed"));
    } finally { setPeerSubmitting(false); }
  };

  const secs = (elapsed / 1000).toFixed(1);
  const targetMs = subsetTargetSec * 1000;
  const pct = Math.min(100, Math.round(100 * elapsed / Math.max(targetMs, 1)));

  return (
    <>
      {hasSections && !sectionUnlocked && (
        <div data-testid="section-locked-hint" className="mb-4 flex items-center gap-2 text-xs text-[#B45309] bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-3 py-2">
          <Lock className="w-3.5 h-3.5 flex-shrink-0" /> {t("rd.section_locked_message")}
        </div>
      )}
      {hasSections && sectionUnlocked && (
        <div data-testid="section-gating-hint" className="mb-4 text-xs text-[#064E3B] bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl px-3 py-2">
          {t("rd.section_gating_hint")}
        </div>
      )}

      {/* Sticky control bar — stays visible while scrolling through the
          item grid below, so timer/start-stop/font controls are always
          reachable while reading. */}
      <div className="sticky sticky-below-header z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-2 pb-2 bg-[#FDFBF7]/95 backdrop-blur supports-[backdrop-filter]:bg-[#FDFBF7]/85" data-testid="sticky-toolbar">
        <div className="rounded-2xl bg-white border border-[#E7E0D2] shadow-sm p-3 sm:p-4">
          {mode !== "running" && !recUrl && !recError && (
            <div data-testid="read-aloud-hint" className="mb-3 flex items-start gap-2 text-xs text-[#064E3B] bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl px-3 py-2">
              <Mic className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span dangerouslySetInnerHTML={{ __html: t("rd.hint_main_html") }} />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Hidden while a test is actively running — switching font
                  size mid-test is harmless, but keeping the control bar
                  minimal during an active recording avoids stray clicks. */}
              {mode !== "running" && (
                <div className="inline-flex items-center rounded-full border border-[#E7E0D2] overflow-hidden" data-testid="font-size-control" title={t("rd.font_title")}>
                  <button data-testid="font-smaller" onClick={() => setFontStep((s) => Math.max(-1, s - 1))}
                    className="px-2.5 py-2 text-sm text-[#4B5563] hover:bg-[#FFF7ED] hover:text-[#1F2937] transition" aria-label={t("rd.smaller")}>A−</button>
                  <span className="px-1.5 text-[10px] text-[#4B5563] tabular-nums">{t("rd.font_size_label")}</span>
                  <button data-testid="font-larger" onClick={() => setFontStep((s) => Math.min(2, s + 1))}
                    className="px-2.5 py-2 text-sm text-[#4B5563] hover:bg-[#FFF7ED] hover:text-[#1F2937] transition" aria-label={t("rd.larger")}>A+</button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2" data-testid="timer-area">
              {mode === "running" && !recError && (
                <span data-testid="rec-active-chip" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FEF2F2] text-[#EF4444] text-[11px] font-semibold border border-[#FECACA]" title={t("rd.rec_active_title")}>
                  <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" /> {t("rd.rec")}
                </span>
              )}
              {mode === "running" && recError && (
                <span data-testid="rec-fail-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#4B5563] text-[10px] font-medium" title={recError}>
                  <Mic className="w-3 h-3" /> {t("rd.mic_off")}
                </span>
              )}
              {expertSec > 0 && (
                <span data-testid="timer-min-chip" className="inline-flex flex-col items-center px-2 py-1 rounded-lg bg-[#FEF2F2] border border-[#EF4444]/40 leading-none">
                  <span className="text-[8px] uppercase tracking-wider text-[#EF4444] font-bold">Min</span>
                  <span className="text-xs font-semibold text-[#B91C1C] tabular-nums">{expertSec}s</span>
                </span>
              )}
              <div className="font-heading text-2xl sm:text-3xl font-light tabular-nums text-[#064E3B]">{secs}<span className="text-sm text-[#4B5563]">s</span></div>
              {subsetTargetSec > 0 && (
                <span data-testid="timer-max-chip" className="inline-flex flex-col items-center px-2 py-1 rounded-lg bg-[#FEF2F2] border border-[#EF4444]/40 leading-none">
                  <span className="text-[8px] uppercase tracking-wider text-[#EF4444] font-bold">Max</span>
                  <span className="text-xs font-semibold text-[#B91C1C] tabular-nums">{subsetTargetSec}s</span>
                </span>
              )}
              {mode === "running" && <div className="flex items-end gap-0.5 h-7">
                {[0,1,2,3].map(i => <span key={i} className="wave-bar w-1 bg-[#D97706] rounded-full" style={{ height: "100%", animationDelay: `${i*0.1}s` }} />)}
              </div>}
            </div>

            <div className="flex items-center gap-2">
              {mode !== "running" && (
                sectionUnlocked ? (
                  <button data-testid="start-btn" onClick={start}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white font-medium transition shadow-sm">
                    <Play className="w-4 h-4" /> {t("rd.test")}
                  </button>
                ) : (
                  <span data-testid="start-btn-locked" title={t("rd.section_locked_message")}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E7E0D2] text-[#4B5563] font-medium cursor-not-allowed">
                    <Lock className="w-4 h-4" /> {t("rd.section_locked_title")}
                  </span>
                )
              )}
              {mode === "running" && (
                <button data-testid="stop-btn" onClick={stop}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-medium transition shadow-sm">
                  <Square className="w-4 h-4" /> {t("rd.stop_test")}
                </button>
              )}
              {(mode === "done" || mode === "running") && (
                <button onClick={reset} className="inline-flex items-center gap-2 px-3 py-2.5 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] transition" title={t("rd.reset_title")}>
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {m.target_seconds > 0 && (mode === "running" || mode === "done") && (
            <div className="mt-3 h-1.5 bg-[#E7E0D2] rounded-full overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${pct}%`, background: pct > 100 ? "#EF4444" : "#064E3B" }} />
            </div>
          )}

          {mode === "done" && (recUrl || recError) && (
            <div className="mt-3 pt-3 border-t border-[#E7E0D2]" data-testid="recording-controls">
              {recUrl ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
                    <Mic className="w-3 h-3" /> {t("rd.your_recording")}
                  </span>
                  <audio data-testid="recording-audio" controls preload="metadata" src={recUrl} className="h-9 max-w-xs" title={t("rd.your_recording_title")}>
                    {t("rd.audio_unsupported")}
                  </audio>
                </div>
              ) : (
                <div data-testid="recording-error" className="flex items-start gap-2 text-xs text-[#B45309] bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-3 py-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{recError}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        {mode === "done" && result && (
          <div data-testid="result-indicator" className={`rounded-2xl p-5 border-2 ${result.attempt.passed ? "bg-[#ECFDF5] border-[#10B981]" : "bg-[#FEF2F2] border-[#EF4444]"}`}>
            <div className="flex items-start gap-3 flex-wrap">
              {result.attempt.passed ? <CheckCircle2 className="w-8 h-8 text-[#10B981] flex-shrink-0" /> : <XCircle className="w-8 h-8 text-[#EF4444] flex-shrink-0" />}
              <div className="flex-1 min-w-[200px]">
                <div className="font-heading text-xl font-semibold text-[#1F2937]" data-testid="result-headline">
                  {result.attempt.passed ? t("rd.result_passed") : t("rd.result_failed")}
                </div>
                <div className="mt-1 text-sm text-[#1F2937]" data-testid="result-sub-message">
                  {result.attempt.passed
                    ? t("rd.result_passed_message")
                    : result.attempt.fail_reason === "too_fast"
                      ? t("rd.result_failed_message_too_fast", { min: Math.ceil(subsetTargetSec * MIN_ATTEMPT_RATIO) })
                      : t("rd.result_failed_message", { target: subsetTargetSec })}
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-1.5 text-sm">
                  <div data-testid="result-your-time" className="text-[#1F2937]">{t("rd.result_your_time", { s: (elapsed / 1000).toFixed(1) })}</div>
                  <div data-testid="result-target-time" className="text-[#1F2937]">{t("rd.result_target_time", { target: subsetTargetSec })}</div>
                  {subsetTargetSec > 0 && (
                    <div data-testid="result-min-time" className="text-[#1F2937]">{t("rd.result_min_time", { min: Math.ceil(subsetTargetSec * MIN_ATTEMPT_RATIO) })}</div>
                  )}
                  {expertSec > 0 && (
                    <div data-testid="result-expert-time" className="text-[#D97706]">{t("rd.result_expert_time", { expert: expertSec })}</div>
                  )}
                </div>

                {(sectionProgress || (isFullSet && (progress?.attempts > 0 || progress?.best_ms))) && (
                  <div data-testid="previous-stats" className="mt-3 flex flex-wrap items-center gap-2">
                    {(() => {
                      const stats = isFullSet ? progress : sectionProgress;
                      return (
                        <>
                          {stats?.best_ms && (
                            <span data-testid="previous-best" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-current/30 text-[11px] font-medium text-[#1F2937]">
                              <Trophy className="w-3 h-3 text-[#D97706]" /> {t("rd.result_best_label")}: {(stats.best_ms / 1000).toFixed(1)}s
                            </span>
                          )}
                          {stats?.attempts > 0 && (
                            <span data-testid="previous-attempts" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-current/30 text-[11px] font-medium text-[#1F2937]">
                              {t("rd.result_attempts_label")}: {stats.attempts}
                            </span>
                          )}
                          {stats?.first_completed_at && (
                            <span data-testid="previous-first-passed" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-current/30 text-[11px] font-medium text-[#1F2937]">
                              <Award className="w-3 h-3 text-[#10B981]" /> {t("rd.result_first_passed_label")}: {new Date(stats.first_completed_at).toLocaleDateString()}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
              {!result.attempt.passed && (
                <button data-testid="result-retry-btn" onClick={reset}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white text-sm font-medium transition">
                  <RotateCcw className="w-4 h-4" /> {t("rd.result_retry")}
                </button>
              )}
            </div>

            {result.attempt.passed && recBlob && !peerSubmitted && (
              <div className="mt-5 pt-4 border-t border-current/20" data-testid="post-pass-dialog">
                <div className="text-sm text-[#1F2937] font-medium">{t("rd.postpass_title")}</div>
                <div className="text-xs text-[#4B5563] mt-1">{t("rd.postpass_body")}</div>
                <div className="mt-3">
                  <PeerReviewTargetPicker onChange={setPeerTarget} />
                </div>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <Link data-testid="postpass-continue" to="/dashboard"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] text-sm font-medium transition">
                    {t("rd.postpass_continue")} →
                  </Link>
                  <button data-testid="postpass-peer-review" onClick={submitToPeerReview}
                    disabled={peerSubmitting || !peerTarget.ready}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white text-sm font-medium transition disabled:opacity-60 disabled:cursor-not-allowed">
                    <Users className="w-4 h-4" /> {peerSubmitting ? t("rec.peer_submitting") : t("rd.postpass_peer_send")}
                  </button>
                </div>
              </div>
            )}
            {result.attempt.passed && recBlob && peerSubmitted && (
              <div className="mt-5 pt-4 border-t border-current/20 flex items-center gap-2 flex-wrap" data-testid="post-pass-submitted">
                <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                <span className="text-sm text-[#1F2937]">{t("rec.peer_submitted")}</span>
                <Link to="/dashboard" className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium transition">
                  {t("rd.postpass_continue")} →
                </Link>
              </div>
            )}

            <ModuleFeedbackPrompt key={result.attempt.created_at || `${m.code}-${elapsed}`} moduleCode={m.code} />
          </div>
        )}
      </div>

      {/* Section switching itself now lives in ReadingMode's shared picker
          above Learn/Test — this block just keeps the descriptive caption
          for whichever section is currently selected. */}
      {hasSections && (
        <div className="mt-6 rounded-2xl bg-white border border-[#E7E0D2] p-3 sm:p-4" data-testid="section-caption">
          <div className="text-xs text-[#B45309] bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-3 py-2" dangerouslySetInnerHTML={{ __html: t("rd.subset_caption_html", { sec: selectedSection, target: subsetTargetSec, n: visibleItems.length }) }} />
          {sectionDescription && (
            <div data-testid="section-description" className="mt-2 text-xs text-[#4B5563] px-2">{sectionDescription}</div>
          )}
          {expertSec > 0 && (
            <div data-testid="section-expert-hint" className="mt-1 text-xs text-[#D97706] px-2">{t("rd.section_expert_hint", { expert: expertSec })}</div>
          )}
        </div>
      )}

      <div className="mt-6 rounded-3xl bg-white border border-[#E7E0D2] p-6 sm:p-10 parchment">
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-5 flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" /> {t("rd.read_all_label")}
        </div>
        {m.n >= 6 && (
          <div data-testid="breath-hint" className="mb-4 text-xs text-[#064E3B] bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl px-3 py-2">
            {t("rd.breath_hint")}
          </div>
        )}
        <SectionContent
          module={m}
          section={isFullSet ? null : selectedSection}
          fontSizeClass={fontSizeClass}
          groupFontSizeClass={groupFontSizeClass}
        />
      </div>
    </>
  );
}
